import { useState } from 'react';

const faqs = [
  {
    q: 'How long does the verification take?',
    a: 'The automated BPI audit is completed within 5 minutes. Once you connect your wallet and submit the asset for verification, our proprietary engine performs the full smart contract audit, wallet provenance check, and global blacklist cross-referencing in real time. The BPI Compliance Certificate is issued instantly upon successful completion.',
    tag: 'Process',
  },
  {
    q: 'What tokens are supported?',
    a: 'Currently, we support high-volume USDC (USD Coin, issued by Circle) and USDT (Tether) on the Ethereum (ERC-20) network. Both present the highest institutional risk exposure from counterfeit contracts, blacklisted addresses, and hidden administrative functions. Expansion to additional networks is planned.',
    tag: 'Scope',
  },
  {
    q: 'Why was my transaction flagged?',
    a: "A flag may indicate: (1) the contract address is not the official Tether or Circle deployment, (2) hidden administrative functions exist that allow balance freezing or burning, (3) the wallet address appears on one or more of our 50+ monitored blacklists, or (4) the transaction history is associated with sanctioned or high-risk counterparties. Our system detects if a token is a 'Proxy' or has malicious functions that differ from the official canonical contracts.",
    tag: 'Compliance',
  },
  {
    q: 'Is there a fee for the verification?',
    a: 'Yes. The BPI asset verification fee ranges from $0.10 to $0.80 per audit, depending on the complexity of the smart contract and the depth of the blacklist cross-reference required. This fee covers the full automated deep-scan, wallet provenance check, and issuance of the digitally signed BPI Compliance Certificate. Payment details are provided at the start of the verification session.',
    tag: 'Fees',
  },
  {
    q: 'What is a honey pot smart contract and how is it detected?',
    a: "A honey pot is a malicious smart contract designed to accept deposits but block all withdrawals through obfuscated conditional logic. These contracts appear legitimate in standard wallet interfaces but trap institutional liquidity permanently. BPI's bytecode-level decompilation identifies honey pot conditions by analyzing all exit function logic against known exploitation patterns.",
    tag: 'Security',
  },
  {
    q: 'Is the BPI Certificate recognized by exchanges and legal authorities?',
    a: 'Yes. The BPI Verification Certificate is issued under Swiss law and is accepted by a growing number of regulated cryptocurrency exchanges, OTC desks, institutional custodians, and legal departments. It is admissible in Swiss and EU legal proceedings involving disputed digital assets, and is aligned with FATF Recommendation 16 and Swiss FINMA regulatory requirements.',
    tag: 'Legal',
  },
  {
    q: 'Is my private key or seed phrase ever required?',
    a: "Absolutely not. BPI's verification process is entirely read-only. We never request, transmit, or store private keys, seed phrases, or any signing credentials. The wallet connection is used solely to authenticate the audit session. All communications are encrypted with TLS 1.3 and our system is non-custodial by architecture.",
    tag: 'Security',
  },
  {
    q: 'What happens if my assets are found to be non-compliant?',
    a: 'If the audit reveals that your USDC or USDT is linked to a blacklisted address, contains a compromised smart contract, or fails any compliance check, BPI will issue a detailed Non-Compliance Report specifying the exact nature and severity of each finding. Our compliance team will then guide you through the appropriate remediation steps.',
    tag: 'Process',
  },
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="bg-slate-50 py-16 sm:py-24 px-5 sm:px-10 lg:px-16">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full"></div>
            <span
              className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Professional Q&amp;A
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.08] tracking-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Frequently Asked
              <br />
              <span className="text-emerald-600">Questions.</span>
            </h2>
            <p className="text-slate-500 text-base leading-relaxed max-w-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Everything institutions need to know about ERC-20 asset verification,
              AML compliance, and the BPI certification process.
            </p>
          </div>
        </div>

        {/* Accordion */}
        <div className="space-y-2 mb-10 sm:mb-14">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`bg-white rounded-xl overflow-hidden border transition-all duration-200 ${
                open === i ? 'border-emerald-300' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full text-left px-6 sm:px-8 py-4 sm:py-5 flex items-center gap-4 cursor-pointer"
              >
                <span
                  className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                    open === i
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {faq.tag}
                </span>
                <span
                  className="flex-1 text-slate-900 font-bold text-sm sm:text-base leading-snug"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {faq.q}
                </span>
                <div
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                    open === i ? 'bg-emerald-500 text-white rotate-180' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <i className="ri-arrow-down-s-line text-base"></i>
                </div>
              </button>
              {open === i && (
                <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                  <div className="border-t border-slate-100 pt-4 pl-0 sm:pl-14">
                    <p
                      className="text-slate-600 text-sm sm:text-base leading-relaxed"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {faq.a}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="bg-[#001529] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-emerald-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-xl text-emerald-400"></i>
            </div>
            <div>
              <div className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Still have compliance questions?
              </div>
              <div className="text-slate-400 text-xs sm:text-sm mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                Our BPI Insight AI assistant is available 24/7 for AML and ERC-20 security inquiries.
              </div>
            </div>
          </div>
          <a
            href="https://mycryptoaml.com/verification"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-6 py-3 rounded-lg font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shield-check-line"></i>
            Proceed to Verification
          </a>
        </div>
      </div>
    </section>
  );
}
